import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast, Bounce } from "react-toastify";
import "./followUp.css";
import CustomSelect from "../CustomSelect";
import useTrainerSearch from "../hooks/useTrainerSearch";

const AddFollowUp = () => {
  const [trainerCodiceFiscale, setTrainerCodiceFiscale] = useState("");
  const [dataFormazione, setDataFormazione] = useState("");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inputErrors, setInputErrors] = useState({});
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const loadTrainerOptions = useTrainerSearch();

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per modificare!");
      navigate("/login");
      localStorage.clear();
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);

  const isValid = () => {
    const errors = {};
    if (!trainerCodiceFiscale) {
      errors.trainerCodiceFiscale = "Seleziona un docente!";
    }
    if (!dataFormazione) {
      errors.dataFormazione = "La data formazione è obbligatoria!";
    }

    const today = new Date().toISOString().split("T")[0];

    if (dataFormazione > today) {
      errors.dataFormazione = "La data non può essere nel futuro!";
    }

    if (!location) {
      errors.location = "La posizione non può essere vuota!";
    }

    if (!note) {
      errors.note = "La nota non può essere vuota!";
    }
    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    setLoading(true);
    try {
      const response = await api.post("/followup", {
        trainerCodiceFiscale: trainerCodiceFiscale.trim(),
        dataFormazione,
        location,
        note,
      });
      if (response.status === 200) {
        toast.success("Follow-up creato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
      navigate("/follow-up");
    } catch (error) {
      console.error(error);

      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) return null;

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">
          <i className="fa-solid fa-chart-line"></i>
          Aggiungi Follow-Up
        </div>
        <div className="controls">
          <button onClick={() => navigate("/follow-up")} className="back-btn">
            <i className="fa-solid fa-arrow-left"></i>
            Torna ai Follow-Up
          </button>
        </div>
      </div>

      <div className="page-content add-followup">
        <div className="form-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fa-solid fa-clipboard-check"></i>
              Dettagli Follow-Up
            </div>
            <div className="card-subtitle">
              Registra un nuovo follow-up per monitorare la formazione di un
              docente
            </div>
          </div>

          <div className="card-content">
            <div className="form-field">
              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-user-tie"></i>
                  Docente
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <CustomSelect
                    loadOptions={loadTrainerOptions}
                    value={selectedTrainer}
                    onChange={(selected) => {
                      setSelectedTrainer(selected);
                      const codiceFiscale =
                        selected?.trainer?.codiceFiscale || selected?.value || "";
                      setTrainerCodiceFiscale(codiceFiscale);
                      setInputErrors((prev) => {
                        const updated = { ...prev };
                        delete updated.trainerCodiceFiscale;
                        return updated;
                      });
                    }}
                    placeholder="Seleziona un docente"
                    className="modern-select"
                  />
                  {inputErrors.trainerCodiceFiscale && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.trainerCodiceFiscale}
                    </span>
                  )}
                  {error && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {error}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-calendar-alt"></i>
                  Data Formazione
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <input
                    type="date"
                    value={dataFormazione}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDataFormazione(val);
                      setInputErrors((prev) => {
                        const updated = { ...prev };
                        if (val) {
                          const today = new Date().toISOString().split("T")[0];
                          if (val <= today) delete updated.dataFormazione;
                        }
                        return updated;
                      });
                    }}
                    className="modern-input"
                  />
                  {inputErrors.dataFormazione && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.dataFormazione}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-map-marker-alt"></i>
                  Location
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLocation(val);
                      if (val.trim()) {
                        setInputErrors((prev) => {
                          const updated = { ...prev };
                          delete updated.location;
                          return updated;
                        });
                      }
                    }}
                    placeholder="Inserisci la location"
                    className="modern-input"
                  />
                  {inputErrors.location && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.location}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field full-width">
                <label className="form-label">
                  <i className="fa-solid fa-sticky-note"></i>
                  Note
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <textarea
                    value={note}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNote(val);
                      if (val.trim()) {
                        setInputErrors((prev) => {
                          const updated = { ...prev };
                          delete updated.note;
                          return updated;
                        });
                      }
                    }}
                    placeholder="Inserisci le note del follow-up"
                    className="modern-textarea"
                    rows={4}
                  />
                  {inputErrors.note && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.note}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={() => navigate("/follow-up")}
              >
                <i className="fa-solid fa-times"></i>
                Annulla
              </button>
              <button className="btn-primary" onClick={handleSubmit}>
                <i className="fa-solid fa-save"></i>
                Crea Follow-Up
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFollowUp;
